import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import fileExtension from 'file-extension';

import { makeGetMessagesByTypeAndAssociationId, makeGetNonSystemMessagesByTypeAndAssociationId } from '../selectors/messages';
import { makeGetMessageNotificationsByAssociationId } from '../selectors/notifications';
import { makeGetCurrentUserId } from '../selectors/users';
import { makeGetFetchedState, makeGetLoadingState, makeGetHasAfter, makeGetHasBefore } from '../selectors/global';

import { fetchMessages, fetchMessage, sendMessage, seeMessage } from '../actions/messages';
import { readNotifications } from '../actions/notifications';

import { debugLog as log } from 'shared-services/logger-service';

import config from '../config';
import { trackEvent } from '../services/events-tracker-service';
import { serializeElement } from '../utils/html';
import { getTypeKey, getFetchKey } from '../utils/message';
import { emojisRegex } from '../md/plugins/emoji';

import DiscussionNotificationsBar from './discussion-notifications-bar';
import DiscussionDayGroup from './discussion-day-group';
import DiscussionInput from './discussion-input';
import ZoomBanner from './zoom-banner';
import Zoom from './zoom';

import IconChatBubble from '../icons/icon-chat-bubble';
import IconClose from '../icons/icon-close';

import MessagePlaceholder from './placeholders/message.placeholder';

const MSG_LIMIT = 15;
const SCROLL_LOAD_MORE_THRESHOLD = 1000;
const SCROLL_TO_MESSAGE_OFFSET = 34;
const SCROLL_TO_BOTTOM_THRESHOLD = 200;
const AUTOFETCHER_INTERVAL = 1000 * 60;

class Discussion extends Component {
    static propTypes = {
        systemAsAction: PropTypes.bool,
        botUseUserName: PropTypes.bool,
        smallerSystem: PropTypes.bool,
        canCreateTask: PropTypes.bool,
        compact: PropTypes.bool,
        compactInput: PropTypes.bool,
        directUpload: PropTypes.bool,
        dateFormat: PropTypes.string,
        emptyComponent: PropTypes.element,
        filePreviewPath: PropTypes.string,
        gifSize: PropTypes.string,
        headerEnabled: PropTypes.bool,
        hideSystem: PropTypes.bool,
        id: PropTypes.string,
        docId: PropTypes.string,
        inputPlaceholer: PropTypes.string,
        localDropzone: PropTypes.bool,
        noDivider: PropTypes.bool,
        controlDeleteText: PropTypes.string,
        onBlur: PropTypes.func,
        onClose: PropTypes.func,
        onFocus: PropTypes.func,
        onHeaderClick: PropTypes.func,
        onNewMessage: PropTypes.func,
        onThreadActivate: PropTypes.func,
        onPinMessage: PropTypes.func,
        preventInitialFocus: PropTypes.bool,
        projectId: PropTypes.string,
        provideController: PropTypes.func,
        showSendToggle: PropTypes.bool,
        startFromMessage: PropTypes.string,
        subtitle: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
        urlPreviewEnabled: PropTypes.bool,
        users: PropTypes.array,
        utils: PropTypes.arrayOf(PropTypes.string),
        fetched: PropTypes.bool,
        hasAfter: PropTypes.bool,
        hasBefore: PropTypes.bool,
        loading: PropTypes.bool,
        messages: PropTypes.array,
        notifications: PropTypes.array,
        zoomId: PropTypes.string,
        zoomPassword: PropTypes.string,
        pinnedMessage: PropTypes.string,
        pinnedMessageBy: PropTypes.string
    };

    static defaultProps = {
        headless: false,
        headerEnabled: false,
        gifSize: '',
        emptyText: 'Post a message by typing below.'
    };

    state = {
        meetingJoined: false
    }

    constructor(props) {
        super(props);

        this.lastScrollPos = 0;
        this.pinToMessage = null;
        this.fetchInterval = null;
        this.freezePosition = null;
    }

    handleNewMessage = (message, files = []) => {
        this.addMessage({
            files,
            ...message
        })

        if (this.props.type === 'chat') {
            trackEvent('CHAT.POST');
        } else {
            trackEvent('DISCUSSION.POST');
        }

        files.forEach(file => {
            trackEvent('FILE.UPLOAD', {
                via: 'Discussion',
                id: file.id,
                fileType: fileExtension(file.name),
                fileSize: file.size
            });
        });
    }

    handleDiscussionScroll = event => {
        if (this.freezePosition !== null) {
            this.scrollElement.scrollTop = this.freezePosition;
            return;
        }

        this.loadMoreMessages(this.shouldLoadMore(true));
    }

    handleNotificationsBarClick = () => {
        this.scrollToTop();
    }

    handleNotificationsBarClose = () => {
        this.markNotificationsAsRead(this.props.notifications);
    }

    handleDiscussionCopy = event => {
        const serializedContent = serializeElement(document.getSelection().getRangeAt(0).cloneContents());
        event.clipboardData.setData('text/plain', serializedContent);
        event.preventDefault();
    }

    handleHeightChange = () => {
        if (this.shouldScrollBottom) {
            this.scrollToBottom();
        }
    }

    handleMeetingJoin = () => {
        if (!config.ZOOM_USE_SDK) {
            const win = window.open(this.props.zoomJoinUrl, '_blank');
            win && win.focus();
        } else {
            this.setState({
                meetingJoined: true
            });
        }
    }

    handleMeetingLeave = () => {
        if (this.unmounted) {
            return;
        }

        if (!this.state.meetingJoined) { 
            return;
        }

        this.setState({
            meetingJoined: false
        });
    }

    handleMessageEditStart = messageId => {
        this.scrollToMessage(messageId, true);
    }

    setInputCtrl = ctrl => this.inputCtrl = ctrl

    loadMoreMessages({ before, after }) {
        if (before) {
            this.loadMessagesBefore();
        }

        if (after) {
            this.loadMessagesAfter();
        }
    }

    markNotificationsAsRead(notifications) {
        readNotifications(notifications.map(notification => notification.id));
    }

    addMessage({ type = 'text', text, url, files, doc_attachment }) {
        const localFiles = (files && files.filter(file => !file.serviceType)) || [];

        this.scrollToBottom();

        sendMessage({
            [getTypeKey(this.props.type)]: this.props.id,
            text,
            type,
            url,
            files,
            document: this.props.docId,
            doc_attachment
        }).then(messageId => {
            if (!doc_attachment) {
                if (text) {
                    trackEvent('MESSAGE.POST', {
                        messageId,
                        type: 'Text message' + (RegExp(emojisRegex, 'g').test(text) ? ' + emoji' : '') + (localFiles.length ? ' + files' : ''),
                        via: this.props.type
                    });
                } else {
                    trackEvent('MESSAGE.POST', {
                        messageId,
                        type: 'Files message',
                        via: this.props.type
                    });
                }
            }
        });

        this.scrollToBottom();

        this.props.onNewMessage && this.props.onNewMessage();
    }

    loadMessagesBefore() {
        if (!this.props.loading && this.props.hasBefore) {
            const beforeMessage = this.props.messages[this.props.messages.length - 1];
            this.fetchMessages({
                before_date: beforeMessage && beforeMessage.created_at
            });
        }
    }

    loadMessagesAfter() {
        if (!this.props.loading && this.props.hasAfter) {
            const afterMessage = this.props.messages[0];
            this.fetchMessages({
                after_date: afterMessage && afterMessage.created_at
            });
        }
    }

    shouldLoadMore(checkDirection) {
        if (!this.props.id || !this.scrollElement) {
            return false;
        }

        const elementHeight = this.scrollElement.clientHeight;
        const scrollHeight = this.scrollElement.scrollHeight;
        const newScrollPos = this.scrollElement.scrollTop;
        const scrollUp = this.lastScrollPos > newScrollPos;
        const scrollDown = this.lastScrollPos < newScrollPos;

        this.lastScrollPos = newScrollPos;

        return {
            before: this.props.hasBefore && newScrollPos < SCROLL_LOAD_MORE_THRESHOLD && (!checkDirection || (checkDirection && scrollUp)),
            after: this.props.hasAfter && scrollHeight - (newScrollPos + elementHeight) < SCROLL_LOAD_MORE_THRESHOLD && (!checkDirection || (checkDirection && scrollDown))
        }
    }

    scrollToTop() {
        this.scrollTo(0);
    }

    scrollToBottom() {
        this.scrollTo(this.scrollElement && this.scrollElement.scrollHeight);
    }

    groupMessagesByDay(messages) {
        const groups = [];

        messages.forEach(message => {
            const day = moment(message.created_at).startOf('day').valueOf();
            let selectedGroup = groups.find(group => group.day === day);

            if (!selectedGroup) {
                selectedGroup = {
                    day,
                    items: []
                };

                groups.push(selectedGroup);
            }

            selectedGroup.items.push(message);
        });

        return groups;
    }

    get emptyComponent() {
        return this.props.emptyComponent || (
            <div className="discussion-add-new">
                <IconChatBubble />

                <p>{this.props.emptyText}</p>
            </div>
        )
    }

    fetchMessage(messageId) {
        if (!this.props.id) {
            return Promise.reject();
        }

        return fetchMessage(messageId);
    }

    fetchMessages({ before_date, after_date, before_id, after_id } = {}, autoFetch) {
        if (!this.props.id) {
            return Promise.reject();
        }

        return fetchMessages({
            [getFetchKey(this.props.type)]: this.props.id,
            limit: MSG_LIMIT,
            before_date,
            after_date,
            before_id,
            after_id
        });
    }

    attemptFetch() {
        if (!this.props.id || this.props.fetched || this.props.loading || this.pinToMessage) {
            return;
        }

        if (this.props.startFromMessage && !this.fetchedForMessage) {
            this.loadForMessage(this.props.startFromMessage);
            return;
        }

        this.fetchMessages()
            .catch(error => {
                if (error === 'Chat not found') {
                    this.props.onNotFound && this.props.onNotFound();
                }
            });
    }

    async loadForMessage(messageId) {
        this.fetchedForMessage = true;
        this.pinToMessage = messageId;

        const messageFromStore = this.props.messages.find(message => message.id === messageId);
        const message = messageFromStore || await this.fetchMessage(messageId);

        this.scrollToMessage(messageId);

        await this.fetchMessages({ before_date: message.created_at });
        this.scrollToMessage(messageId);

        await this.fetchMessages({ after_date: message.created_at });
        this.scrollToMessage(messageId);

        setTimeout(() => {
            this.pinToMessage = null;
        }, 10000);
    }

    scrollToMessage(messageId, ifNeeded) {
        if (!this.scrollElement) {
            return;
        }

        const element = this.scrollElement.querySelector(`[data-message-id="${messageId}"]`);

        if (!element) {
            return;
        }

        const scrollTop = this.scrollElement.scrollTop;
        const scrollRect = this.scrollElement.getBoundingClientRect();
        const messageRect = element.getBoundingClientRect();

        if (ifNeeded) {
            if (messageRect.bottom < scrollRect.bottom) {
                return;
            }
        }

        this.scrollTo(scrollTop + messageRect.top - scrollRect.top - SCROLL_TO_MESSAGE_OFFSET);

    }

    readMessagesNotifications() {
        if (!this.props.notifications.length || !this.messages.length) {
            return;
        }

        this.markNotificationsAsRead(this.props.notifications.filter(notification => notification && this.messages.some(message => message.id === notification.association.message_id)));
    }

    isMessageSeen(message) {
        if (!message) {
            return true;
        }

        return message.author === this.props.currentUserId || (message.seen_by || []).includes(this.props.currentUserId);
    }

    seeMessage(messageId) {
        seeMessage(messageId);
    }

    startAutoFetcher() {
        if (this.fetchInterval || !this.endReached) {
            return;
        }

        this.fetchInterval = setInterval(() => this.fetchMessages({}, true), AUTOFETCHER_INTERVAL);
    }

    stopAutoFetcher() {
        if (!this.fetchInterval) {
            return;
        }

        clearInterval(this.fetchInterval);
        this.fetchInterval = null;
    }

    scrollTo(scrollTo) {
        if (!this.scrollElement) {
            return;
        }

        this.scrollElement.scrollTop = scrollTo;

        if (this.freezePosition !== null) {
            this.freezePosition = scrollTo;
        }
    }

    freezeScroll() {
        if (!this.scrollElement) {
            return;
        }

        this.scrollElement.style.overflow = 'hidden';
        this.freezePosition = this.scrollElement.scrollTop;
    }

    unfreezeScroll() {
        if (!this.scrollElement || this.freezePosition === null) {
            return;
        }

        this.scrollElement.style.overflow = 'auto';
        this.freezePosition = null;
    }

    focus() {
        this.inputCtrl && this.inputCtrl.focus();
    }

    get messages() {
        return this.props.messages;
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            shouldLoadMore: checkDirection => {
                return this.shouldLoadMore(checkDirection);
            },
            focus: () => {
                log('[DISCUSSION] Focus from controller');
                this.focus();
            },
            addMessage: (text, files) => {
                this.addMessage({
                    text,
                    files
                });
            }
        });

        if (!this.props.preventInitialFocus) {
            setTimeout(() => {
                log('[DISCUSSION] Focus from autofocus');
                this.focus();
            }, 100);
        }

        if (this.props.startFromMessage && !this.fetchedForMessage) {
            this.loadForMessage(this.props.startFromMessage);
        } else {
            this.endReached = true;
            this.attemptFetch();
            this.scrollToBottom();
        }

        if (
            this.props.messages.length &&
            this.props.messages[0].id &&
            !this.isMessageSeen(this.props.messages[0])
        ) {
            this.seeMessage(this.props.messages[0].id);
        }

        this.readMessagesNotifications();
        this.startAutoFetcher();
    }

    componentWillReceiveProps(nextProps) {

        if (this.props.messages !== nextProps.messages) {
            this.freezeScroll();
        }

        if (this.scrollElement) {
            this.scrollTopBeforeUpdate = this.scrollElement.scrollTop;
            this.scrollHeightBeforeUpdate = this.scrollElement.scrollHeight;
            this.shouldScrollBottom = !this.pinToMessage && this.scrollHeightBeforeUpdate - (this.scrollElement.scrollTop + this.scrollElement.offsetHeight) <= SCROLL_TO_BOTTOM_THRESHOLD;

            if (!this.shouldScrollBottom) {
                log('[DISCUSSION] Scroll to bottom disabled. Reason: ', this.pinToMessage ? 'pinned message ' + this.pinToMessage : 'above threshold: ' + (this.scrollHeightBeforeUpdate - (this.scrollElement.scrollTop + this.scrollElement.offsetHeight)) + ', BREAKDOWN: scrollHeightBeforeUpdate ' + this.scrollHeightBeforeUpdate + ' scrollTop ' + this.scrollElement.scrollTop + ' offsetHeight ' + this.scrollElement.offsetHeight);
                // this.freezeScroll();
            }
        }
    }

    componentWillUnmount() {
        this.stopAutoFetcher();
        this.props.provideController && this.props.provideController(null);
        this.unmounted = true;
    }

    componentDidUpdate(prevProps) {
        if (this.shouldScrollBottom) {
            this.scrollToBottom();
        } else if (this.scrollElement) {
            if (!(prevProps.messages[0] && this.props.messages[0] && prevProps.messages[0].id !== this.props.messages[0].id)) {
                this.scrollTo(this.scrollTopBeforeUpdate + (this.scrollElement.scrollHeight - this.scrollHeightBeforeUpdate));
            }
        }

        if (this.props.startFromMessage && this.props.startFromMessage !== prevProps.startFromMessage && !this.fetchedForMessage) {
            this.loadForMessage(this.props.startFromMessage);
        } else {
            this.attemptFetch();
        }

        this.readMessagesNotifications();

        if (
            this.props.messages !== prevProps.messages &&
            this.props.messages.length &&
            this.props.messages[0].id &&
            !this.isMessageSeen(this.props.messages[0])
        ) {
            this.seeMessage(this.props.messages[0].id);
        }

        if (!prevProps.fetched && this.props.fetched) {
            this.scrollToBottom();
        }

        if (this.props.fetched) {
            this.loadMoreMessages(this.shouldLoadMore());
        }

        if (this.pinToMessage) {
            this.scrollToMessage(this.pinToMessage);
        }

        if (!this.props.hasAfter) {
            this.endReached = true;
        }

        requestAnimationFrame(() => {
            this.unfreezeScroll();
        });
        this.startAutoFetcher();
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (
            (this.props.systemAsAction !== nextProps.systemAsAction) ||
            (this.props.botUseUserName !== nextProps.botUseUserName) ||
            (this.props.smallerSystem !== nextProps.smallerSystem) ||
            (this.props.canCreateTask !== nextProps.canCreateTask) ||
            (this.props.compact !== nextProps.compact) ||
            (this.props.compactInput !== nextProps.compactInput) ||
            (this.props.directUpload !== nextProps.directUpload) ||
            (this.props.dateFormat !== nextProps.dateFormat) ||
            (this.props.filePreviewPath !== nextProps.filePreviewPath) ||
            (this.props.gifSize !== nextProps.gifSize) ||
            (this.props.headerEnabled !== nextProps.headerEnabled) ||
            (this.props.hideSystem !== nextProps.hideSystem) ||
            (this.props.id !== nextProps.id) ||
            (this.props.docId !== nextProps.docId) ||
            (this.props.inputPlaceholer !== nextProps.inputPlaceholer) ||
            (this.props.localDropzone !== nextProps.localDropzone) ||
            (this.props.noDivider !== nextProps.noDivider) ||
            (this.props.controlDeleteText !== nextProps.controlDeleteText) ||
            (this.props.onBlur !== nextProps.onBlur) ||
            (this.props.onClose !== nextProps.onClose) ||
            (this.props.onFocus !== nextProps.onFocus) ||
            (this.props.onHeaderClick !== nextProps.onHeaderClick) ||
            (this.props.onNewMessage !== nextProps.onNewMessage) ||
            (this.props.onThreadActivate !== nextProps.onThreadActivate) ||
            (this.props.onPinMessage !== nextProps.onPinMessage) ||
            (this.props.preventInitialFocus !== nextProps.preventInitialFocus) ||
            (this.props.projectId !== nextProps.projectId) ||
            (this.props.provideController !== nextProps.provideController) ||
            (this.props.showSendToggle !== nextProps.showSendToggle) ||
            (this.props.startFromMessage !== nextProps.startFromMessage) ||
            (this.props.subtitle !== nextProps.subtitle) ||
            (this.props.title !== nextProps.title) ||
            (this.props.type !== nextProps.type) ||
            (this.props.urlPreviewEnabled !== nextProps.urlPreviewEnabled) ||
            (this.props.users !== nextProps.users) ||
            (this.props.utils !== nextProps.utils) ||
            (this.props.fetched !== nextProps.fetched) ||
            (this.props.hasAfter !== nextProps.hasAfter) ||
            (this.props.hasBefore !== nextProps.hasBefore) ||
            (this.props.loading !== nextProps.loading) ||
            (this.props.messages !== nextProps.messages) ||
            (this.props.notifications !== nextProps.notifications) ||
            (this.props.emptyText !== nextProps.emptyText) ||
            (this.props.emptyComponent !== nextProps.emptyComponent) ||
            (this.props.zoomId !== nextProps.zoomId) ||
            (this.props.zoomPassword !== nextProps.zoomPassword) ||
            (this.props.pinnedMessage !== nextProps.pinnedMessage) ||
            (this.props.pinnedMessageBy !== nextProps.pinnedMessageBy) ||
            (this.props.zoomJoinUrl !== nextProps.zoomJoinUrl) ||
            (this.state.meetingJoined !== nextState.meetingJoined)
        ) {
            return true;
        };

        return false;
    }

    render() {
        if (this.props.headless) {
            return null;
        }

        const messageGroups = this.groupMessagesByDay([...this.messages].reverse());

        return (
            <div className={'discussion' + (this.props.compact ? ' compact' : '') + (this.props.loading ? ' loading' : '') + (this.props.noDivider ? ' no-divider' : '') + (this.props.systemAsAction ? ' system-as-action' : '') + (this.props.smallerSystem ? ' smaller-system' : '')} onCopy={this.handleDiscussionCopy}>

                {
                    this.props.headerEnabled ? (
                        <div className="discussion-head" onClick={this.props.onHeaderClick}>
                            <div className="discussion-name">
                                <h3>
                                    <span className="label">
                                        {this.props.title}
                                    </span>
                                </h3>

                                { 
                                    this.props.subtitle ? (
                                        <h4>{this.props.subtitle}</h4>
                                    ) : null
                                }
                            </div>

                            {
                                this.props.onClose ? (
                                    <button className="discussion-close btn btn-small btn-simple-close" onClick={this.props.onClose}>
                                        <IconClose />
                                    </button>
                                ) : null
                            }
                        </div>
                    ) : null
                }

                <ZoomBanner meetingId={this.props.zoomId} onJoin={this.handleMeetingJoin} />

                <Zoom meetingId={this.state.meetingJoined && this.props.zoomId} password={this.props.zoomPassword} onLeave={this.handleMeetingLeave} />
                
                {
                    messageGroups.length ? (
                        <div className="discussion-body" ref={el => this.discussionBody = el}>
                            <DiscussionNotificationsBar notifications={this.props.notifications} onClick={this.handleNotificationsBarClick} onClose={this.handleNotificationsBarClose} />

                            <div className="discussion-scrollable nice-scroll" ref={el => this.scrollElement = el} onScroll={this.handleDiscussionScroll}>
                                <div className="discussion-scrollable-inner">
                                    {
                                        messageGroups.map(group => <DiscussionDayGroup
                                            key={group.day}
                                            day={group.day}
                                            currentUserId={this.props.currentUserId}
                                            messages={group.items}
                                            filePreviewPath={this.props.filePreviewPath}
                                            canCreateTask={this.props.canCreateTask}
                                            dateFormat={this.props.dateFormat}
                                            projectId={this.props.projectId}
                                            discussionId={this.props.id}
                                            discussionName={this.props.title}
                                            urlPreviewEnabled={this.props.urlPreviewEnabled}
                                            gifSize={this.props.gifSize}
                                            users={this.props.users}
                                            noDivider={this.props.noDivider}
                                            systemAsAction={this.props.systemAsAction}
                                            botUseUserName={this.props.botUseUserName}
                                            controlDeleteText={this.props.controlDeleteText}
                                            pinnedMessage={this.props.pinnedMessage}
                                            pinnedMessageBy={this.props.pinnedMessageBy}
                                            onThreadActivate={this.props.onThreadActivate}
                                            onPinMessage={this.props.onPinMessage}
                                            onEditStart={this.handleMessageEditStart}
                                        />)
                                    }
                                </div>
                            </div>
                        </div>
                    ) : (
                        !this.props.fetched && this.props.id ? (
                            <div className="discussion-body">
                                <div className="discussion-scrollable nice-scroll">
                                    <div className="discussion-scrollable-inner">
                                        <MessagePlaceholder key={0} />

                                        <MessagePlaceholder key={1} />

                                        <MessagePlaceholder key={2} />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="discussion-body empty">
                                {this.emptyComponent}
                            </div>
                        )
                    )
                }

                <div className="discussion-foot">
                    <DiscussionInput
                        key={this.props.id}
                        type={this.props.type}
                        compact={this.props.compactInput}
                        directUpload={this.props.directUpload}
                        placeholder={this.props.inputPlaceholer}
                        projectId={this.props.projectId}
                        discussionName={this.props.title}
                        autofocus={this.props.autofocus}
                        utils={this.props.utils}
                        discussionId={this.props.id}
                        showSendToggle={this.props.showSendToggle}
                        localDropzone={this.props.localDropzone}
                        users={this.props.users}
                        onNewMessage={this.handleNewMessage}
                        onDocCreate={this.handleDocCreate}
                        onFocus={this.props.onFocus}
                        onBlur={this.props.onBlur}
                        onHeightChange={this.handleHeightChange}
                        provideController={this.setInputCtrl}
                    />
                </div>
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();
    const getMessageNotificationsByAssociationId = makeGetMessageNotificationsByAssociationId();

    const getMessagesByTypeAndAssociationId = makeGetMessagesByTypeAndAssociationId();
    const getNonSystemMessagesByTypeAndAssociationId = makeGetNonSystemMessagesByTypeAndAssociationId();
    const getMessagesFetched = makeGetFetchedState();
    const getMessagesLoading = makeGetLoadingState();
    const getMessagesHasBefore = makeGetHasBefore();
    const getMessagesHasAfter = makeGetHasAfter();

    return ({ users, discussions, messages, notifications }, ownProps) => {
        return {
            currentUserId: getCurrentUserId(users),
            messages: (ownProps.hideSystem ? getNonSystemMessagesByTypeAndAssociationId : getMessagesByTypeAndAssociationId)(messages.items, ownProps.type, ownProps.id, ownProps.excludeRemoved),
            fetched: getMessagesFetched(messages.fetchInfo, 'association', ownProps.id),
            loading: getMessagesLoading(messages.fetchInfo, 'association', ownProps.id),
            hasBefore: getMessagesHasBefore(messages.fetchInfo, 'association', ownProps.id),
            hasAfter: getMessagesHasAfter(messages.fetchInfo, 'association', ownProps.id),
            notifications: getMessageNotificationsByAssociationId(notifications.items, ownProps.id)
        }
    }
}

export default connect(makeMapStateToProps)(Discussion);
